import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import MGrid, { ACTION_ADD, FILTER_TYPE_NONE, FILTER_TYPE_TEXT, MGridColumnDefinition } from "components/Shared/MGrid";
import { defaultHeightStyle } from "styles/sharedStyles";
import { UNDERWRITER_EDIT } from "typeConstants/rights";
import Card from "@mui/material/Card";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { InternetUser, UnderwriterEditDto, UserUnderwriter } from "interfaces/Mueller.Business.Models";
import { MSpinner } from "components/Shared";
import { DATA_TYPE_BUTTON } from "typeConstants/dataTypes";
import UnderwriterEditForm from "./UnderwriterEditForm";
import { getUnderwriters, getAuthorizedUnderwriters, addInternetUser, removeInternetUser } from "actions/underwriter";
import UserUnderwriterDeleteDialog from "./UserUnderwriterDeleteDialog";
import UserUnderwriterAdd from "./UserUnderwriterAdd";
import { updateUnderwriter } from "requests/underwriter";

interface UserUnderwritersProps {
  gridWrapperStyle?: React.CSSProperties;
  //redux
  userUnderwriters: UserUnderwriter[];
  authorizedUnderwriters: any;
  internetUser: InternetUser;
  internetUserId: Number;
  canEditUnderwriters: boolean;
  isFetchingUnderwriters: boolean;
  getUnderwriters: (internetUserId: Number) => void;
  getAuthorizedUnderwriters: (internetUserId: Number) => void;
  addUnderwriterInternetUser: (underwriterId: Number, internetUserId: Number) => void;
  removeUnderwriterInternetUser: (underwriterId: Number) => void;
}

const UserUnderwriters = (props: UserUnderwritersProps) => {
  const wrapperStyle: React.CSSProperties = props.gridWrapperStyle || defaultHeightStyle;
  const [addUnderwriterDialogBoxVisible, setAddUnderwriterDialogBoxVisible] = useState(false);
  const [editUnderwriterDialogBoxVisible, setEditUnderwriterDialogBoxVisible] = useState(false);
  const [deleteUnderwriterDialogBoxVisible, setDeleteUnderwriterDialogBoxVisible] = useState(false);
  const [selectedOriginalIndex, setSelectedOriginalIndex] = useState(null);
  const mountedRef = useRef<boolean>(false);

  useEffect(() => {
    mountedRef.current = true;
    if (!props.userUnderwriters) {
      props.getUnderwriters(props.internetUserId);
    }
    if (!props.authorizedUnderwriters) {
      props.getAuthorizedUnderwriters(props.internetUserId);
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleAddConfirm = (underwriterId: Number, internetUserId: Number) => {
    props.addUnderwriterInternetUser(underwriterId, internetUserId);
    setAddUnderwriterDialogBoxVisible(false);
  };

  const underwriterSaveCallback = () => {
    props.getUnderwriters(props.internetUserId);
  };

  const onEditUnderwriterSave = (underwriter: UnderwriterEditDto) => {
    setEditUnderwriterDialogBoxVisible(false);
    updateUnderwriter(underwriter.id, underwriter, underwriterSaveCallback, mountedRef);
  };

  const EDIT_COLUMN_KEY = "EDIT_COLUMN_KEY";
  const DELETE_COLUMN_KEY = "DELETE_COLUMN_KEY";

  const getGridColumns = () => {
    const gridCols: MGridColumnDefinition[] = [
      {
        key: "underwriterCode",
        name: "Underwriter Code",
        width: 180,
        dataSource: "underwriterCode",
        resizable: false,
        filterType: FILTER_TYPE_TEXT,
      },
      {
        key: "emailAddress",
        name: "Email",
        width: 180,
        dataSource: "emailAddress",
        resizable: false,
        filterType: FILTER_TYPE_TEXT,
      },
      {
        key: "majorCompany.companyName",
        name: "Company",
        width: 180,
        dataSource: "majorCompany.companyName",
        resizable: false,
        filterType: FILTER_TYPE_TEXT,
      },
    ];

    if (props.canEditUnderwriters) {
      const editButton = () => (
        <EditIcon titleAccess="Edit underwriter" />
      );
      const deleteButton = () => (
        <DeleteIcon titleAccess="Remove underwriter from user" />
      );

      const columnsForEditing: MGridColumnDefinition[] = [
        {
          key: EDIT_COLUMN_KEY,
          name: "Edit",
          width: 100,
          dataSource: editButton,
          dataType: DATA_TYPE_BUTTON,
          resizable: false,
          filterType: FILTER_TYPE_NONE,
        },
        {
          key: DELETE_COLUMN_KEY,
          name: "Delete",
          width: 100,
          dataSource: deleteButton,
          dataType: DATA_TYPE_BUTTON,
          resizable: false,
          filterType: FILTER_TYPE_NONE,
        },
      ];

      gridCols.push(...columnsForEditing);
    }

    return gridCols;
  };

  const handleGridButtons = (selectedOriginalIndex: number, columnKey: string): void => {
    setSelectedOriginalIndex(selectedOriginalIndex);

    if (columnKey === EDIT_COLUMN_KEY) {
      setEditUnderwriterDialogBoxVisible(true);
    }
    if (columnKey === DELETE_COLUMN_KEY) {
      setDeleteUnderwriterDialogBoxVisible(true);
    }
  };

  return (
    <>
      {addUnderwriterDialogBoxVisible ? (
        <UserUnderwriterAdd
          underwriter={props.userUnderwriters[selectedOriginalIndex]}
          onConfirm={handleAddConfirm}
          onCancel={() => setAddUnderwriterDialogBoxVisible(false)}
          visible={addUnderwriterDialogBoxVisible}
        />
      ) : null}
      {editUnderwriterDialogBoxVisible ? (
        <UnderwriterEditForm
          underwriter={props.userUnderwriters[selectedOriginalIndex]}
          onSave={onEditUnderwriterSave}
          onCancel={() => setEditUnderwriterDialogBoxVisible(false)}
          visible={editUnderwriterDialogBoxVisible}
        />
      ) : null}
      {deleteUnderwriterDialogBoxVisible ? (
        <UserUnderwriterDeleteDialog
          underwriter={props.userUnderwriters[selectedOriginalIndex]}
          onConfirm={props.removeUnderwriterInternetUser}
          onCancel={() => setDeleteUnderwriterDialogBoxVisible(false)}
          visible={deleteUnderwriterDialogBoxVisible}
        />
      ) : null}
      {props.isFetchingUnderwriters ? (
        <MSpinner />
      ) : (
        <Card className={`card-unnested profile-card card-style`}>
          <div className="grid-card" style={{ height: "100%" }}>
            <MGrid
              headerText={"Underwriters"}
              columnDefinitions={getGridColumns()}
              data={props.userUnderwriters}
              isFetchingData={false}
              displayNumOfItemsFound={false}
              allowRowSelection={false}
              onCellButtonClick={handleGridButtons}
              wrapperStyle={wrapperStyle}
              actions={[
                {
                  code: ACTION_ADD,
                  onClick: () => setAddUnderwriterDialogBoxVisible(true),
                  icon: "add",
                  buttonText: undefined,
                  helpText: "Add an Underwriter to this user",
                  order: 1,
                  disabled: false,
                },
              ]}
            />
          </div>
        </Card>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    userUnderwriters: state.user.userUnderwriters,
    isFetchingAuthorizedUnderwriters: state.user.isFetchingAuthorizedUnderwriters,
    authorizedUnderwriters: state.user.authorizedUnderwriters,
    canEditUnderwriters: userHasRight(UNDERWRITER_EDIT)(state),
    internetUserId: state.user.internetUserId,
    internetUser: state.user.internetUser,
    isFetchingUnderwriters: state.user.isFetchingUnderwriters,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getUnderwriters(internetUserId) {
      dispatch(getUnderwriters(internetUserId));
    },
    getAuthorizedUnderwriters(internetUserId) {
      dispatch(getAuthorizedUnderwriters(internetUserId));
    },
    addUnderwriterInternetUser(underwriterId, internetUserId) {
      dispatch(addInternetUser(underwriterId, internetUserId));
    },
    removeUnderwriterInternetUser(underwriterId) {
      dispatch(removeInternetUser(underwriterId));
    },
    updateUnderwriter(underwriterUpdateRequestDto) {
      dispatch(updateUnderwriter(underwriterUpdateRequestDto));
    },
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(UserUnderwriters);
